// Or from '@reduxjs/toolkit/query' if not using the auto-generated hooks
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { apiConfig } from "../../apiConfig";
import { getHeaders, ApiScope } from "../silentAuth";
import { TAppRoleDto, ChangeUserRoleCommand } from "../resourceGroups/types";
import { IPerson } from "@oqdo/automationapps/Agents/Person";

// Define a service using a base URL and expected endpoints
export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: fetchBaseQuery({
    baseUrl: apiConfig.userAPiUrl,
    prepareHeaders: async (headers, { getState }) => {
      return await getHeaders(ApiScope, headers);
    },
  }),
  tagTypes: ["users", "userAppRoles"],
  endpoints: (builder) => ({
    getUser: builder.query<IPerson, string | undefined>({
      query: (userId) => (userId ? `users/${userId}` : "user/"),
      providesTags: ["users"],
    }),
    getUsers: builder.query<IPerson[], { tenantId?: string | undefined; mail?: string | undefined }>({
      query: (args) => (args.tenantId ? `users?tenant=${args.tenantId}` : `users?mail=${args.mail}`),
      providesTags: ["users"],
    }),
    getUsersAppRoles: builder.query<TAppRoleDto[], string>({
      query: (userId) => `users/${userId}/appRoles`,
      providesTags: ["userAppRoles"],
    }),

    createUser: builder.mutation<any, CreateUserCommand>({
      query: (args) => ({
        url: `users`,
        method: "post",
        body: args,
        responseHandler: (response: { text: () => any }) => response.text(),
      }),
      onQueryStarted: async function (props, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(userApi.util.invalidateTags(["users"]));
        }, 2000);
      },
      invalidatesTags: ["users"],
    }),
    updateUsersAppRoles: builder.mutation<any, ChangeUserRoleCommand>({
      query: (obj) => ({
        url: `users/${obj.userId}/appRoles`,
        method: "post",
        body: obj,
        responseHandler: (response: { text: () => any }) => response.text(),
      }),
      onQueryStarted: async function (props, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(userApi.util.invalidateTags(["userAppRoles"]));
        }, 2000);
      },
      invalidatesTags: ["userAppRoles"],
    }),
    updateUser: builder.mutation<any, { id: string; patch: any }>({
      query: (obj) => ({
        url: `users/${obj.id}`,
        method: "patch",
        body: obj.patch,
      }),
      onQueryStarted: async function (props, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(userApi.util.invalidateTags(["users"]));
        }, 2000);
      },
      invalidatesTags: ["users"],
    }),
    deleteUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `users/` + id,
        method: "delete",
        responseHandler: (response: { text: () => any }) => response.text(),
      }),
      onQueryStarted: async function (props, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(userApi.util.invalidateTags(["users"]));
        }, 2000);
      },
      invalidatesTags: ["users"],
    }),
    addRelationToUser: builder.mutation<
      any,
      { userId: string; fromObjectId: string; toObjectId: string; type: string }
    >({
      query: (args) => ({
        url: `users/${args.userId}/relations`,
        method: "post",
        body: args,
      }),
      onQueryStarted: async function (props, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(userApi.util.invalidateTags(["users"]));
        }, 2000);
      },
      invalidatesTags: ["users"],
    }),
    removeRelationFromUser: builder.mutation<
      any,
      { userId: string; fromObjectId: string; toObjectId: string; type: string }
    >({
      query: (args) => ({
        url: `users/${args.userId}/relations`,
        method: "delete",
        body: args,
      }),
      onQueryStarted: async function (props, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(userApi.util.invalidateTags(["users"]));
        }, 2000);
      },
      invalidatesTags: ["users"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetUserQuery,
  useGetUsersQuery,
  useGetUsersAppRolesQuery,
  useUpdateUsersAppRolesMutation,
  useDeleteUserMutation,
  useCreateUserMutation,
  useUpdateUserMutation,
  useAddRelationToUserMutation,
  useRemoveRelationFromUserMutation,
} = userApi;

type TargetRelation = {
  sourceId: string;
  relationName: string;
};
type SourceRelation = {
  targetId: string;
  relationName: string;
};

type CreateUserCommand = {
  email: string;
  displayName: string;
  appId: string;
  sourceRelations?: SourceRelation[] | undefined;
  targetRelations?: TargetRelation[] | undefined;
  roleNames?: string[] | undefined;
};
